import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, MapPin } from 'lucide-react';
import { MONTHS_OPTIONS } from '@/lib/qualificationOptions';
import { useQualificationOptionsByKey } from '@/hooks/useQualificationOptions';

export interface USEntry {
  month: number;
  year: number;
  method: string;
}

interface USEntriesListProps {
  entries: USEntry[];
  onChange: (entries: USEntry[]) => void;
  disabled?: boolean;
}

export function USEntriesList({ entries, onChange, disabled = false }: USEntriesListProps) {
  const currentYear = new Date().getFullYear();
  
  // Fetch entry methods from database with fallback
  const { data: entryMethods = [] } = useQualificationOptionsByKey('us_entry_methods');
  
  // Generate year options from current year down to 1970
  const yearOptions = [];
  for (let y = currentYear; y >= 1970; y--) {
    yearOptions.push({ value: y, label: String(y) });
  }

  const addEntry = () => {
    onChange([...entries, { month: 1, year: currentYear, method: '' }]);
  };

  const updateEntry = (index: number, field: keyof USEntry, value: any) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry, index) => (
            <div 
              key={index} 
              className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div className="flex items-center gap-1 text-sm text-muted-foreground w-24 shrink-0">
                <MapPin className="w-4 h-4" />
                <span>Entrada {index + 2}</span>
              </div>
              
              <Select
                value={entry.month?.toString() || ''}
                onValueChange={(v) => updateEntry(index, 'month', parseInt(v))}
                disabled={disabled}
              >
                <SelectTrigger className="w-28 h-8">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={entry.year?.toString() || ''}
                onValueChange={(v) => updateEntry(index, 'year', parseInt(v))}
                disabled={disabled}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={entry.method || ''}
                onValueChange={(v) => updateEntry(index, 'method', v)}
                disabled={disabled}
              >
                <SelectTrigger className="flex-1 h-8">
                  <SelectValue placeholder="Forma de entrada" />
                </SelectTrigger>
              <SelectContent>
                  {entryMethods.map((opt: { value: string; label: string }) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {!disabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEntry(index)}
                  className="shrink-0"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={addEntry} 
          className="gap-1"
        >
          <Plus className="w-4 h-4" />
          Adicionar entrada
        </Button>
      )}
    </div>
  );
}
